import {
  collection,
  doc,
  getDoc,
  getDocs,
  runTransaction,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { trackAppMetric } from "./appMetrics";
import { StaleContentBoxError } from "./contentBoxDocUpdate";
import { stripUndefinedDeep } from "./stripUndefinedDeep";

/**
 * Persist an inner content box update inside a MultiBox document.
 * Throws on missing data or Firestore write failure.
 * Throws StaleContentBoxError when the MultiBox document is already gone.
 */
export async function updateMultiBoxInnerContent({
  userId,
  classId,
  multiBoxId,
  updatedBox,
}) {
  if (!userId || !classId || !multiBoxId || !updatedBox?.contentBoxId) {
    throw new Error("Missing required identifiers for MultiBox inner content save.");
  }

  // Firestore rejects undefined anywhere in the payload, including nested
  // array entities ("Property array contains an invalid nested entity").
  const sanitizedUpdatedBox = stripUndefinedDeep(updatedBox);

  const multiBoxRef = doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    classId,
    FIREBASE_COLLECTIONS.CONTENT_BOXES,
    multiBoxId
  );

  const multiBoxSnap = await getDoc(multiBoxRef);

  if (!multiBoxSnap.exists()) {
    trackAppMetric("content_box.multibox_inner_save", {
      outcome: "stale",
      reason: "missing_doc",
    });
    throw new StaleContentBoxError();
  }

  const multiBoxData = multiBoxSnap.data();

  if (multiBoxData.linkedBox) {
    await updateLinkedMultiBoxInnerContent({
      userId,
      multiBoxId,
      sourceClassId: classId,
      updatedInnerBox: sanitizedUpdatedBox,
    });
    trackAppMetric("content_box.multibox_inner_save", { outcome: "success", linkedBox: true });
    return;
  }

  if (!Array.isArray(multiBoxData.content)) {
    throw new Error("MultiBox content is invalid.");
  }

  const contentBoxIndex = multiBoxData.content.findIndex(
    (box) => box.contentBoxId === sanitizedUpdatedBox.contentBoxId
  );

  if (contentBoxIndex === -1) {
    // Inner box was unstacked/deleted while a nested editor (e.g. whiteboard)
    // still flushed — treat as stale, not a user-visible write failure.
    trackAppMetric("content_box.multibox_inner_save", {
      outcome: "stale",
      reason: "missing_inner",
    });
    throw new StaleContentBoxError();
  }

  const updatedMultiBox = { ...multiBoxData };
  updatedMultiBox.content[contentBoxIndex] = sanitizedUpdatedBox;

  await setDoc(multiBoxRef, updatedMultiBox, { merge: true });
  trackAppMetric("content_box.multibox_inner_save", { outcome: "success", linkedBox: false });
}

/**
 * Propagate an inner content box update across linked MultiBox documents.
 * The source class must contain a valid linked MultiBox with the inner box.
 */
export async function updateLinkedMultiBoxInnerContent({
  userId,
  multiBoxId,
  sourceClassId,
  updatedInnerBox,
}) {
  if (!userId || !multiBoxId || !updatedInnerBox?.contentBoxId) {
    throw new Error("Missing required identifiers for linked MultiBox inner content save.");
  }

  const classesRef = collection(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES
  );
  const classesSnap = await getDocs(classesRef);

  const txs = classesSnap.docs.map((classDoc) => {
    const targetClassId = classDoc.id;
    const boxRef = doc(
      db,
      FIREBASE_COLLECTIONS.USERS,
      userId,
      FIREBASE_COLLECTIONS.CLASSES,
      targetClassId,
      FIREBASE_COLLECTIONS.CONTENT_BOXES,
      multiBoxId
    );

    return runTransaction(db, async (tx) => {
      const snap = await tx.get(boxRef);
      if (!snap.exists()) {
        if (targetClassId === sourceClassId) {
          trackAppMetric("content_box.multibox_inner_save", {
            outcome: "stale",
            reason: "missing_linked_source_doc",
          });
          throw new StaleContentBoxError();
        }
        return;
      }

      const data = snap.data();
      if (!data?.linkedBox || !Array.isArray(data.content)) {
        if (targetClassId === sourceClassId) {
          throw new Error("Linked MultiBox is invalid in source class.");
        }
        return;
      }

      const idx = data.content.findIndex(
        (box) => box?.contentBoxId === updatedInnerBox.contentBoxId
      );
      if (idx === -1) {
        if (targetClassId === sourceClassId) {
          trackAppMetric("content_box.multibox_inner_save", {
            outcome: "stale",
            reason: "missing_linked_inner",
          });
          throw new StaleContentBoxError();
        }
        return;
      }

      const newContent = data.content.slice();
      newContent[idx] = stripUndefinedDeep({
        ...data.content[idx],
        ...updatedInnerBox,
      });

      tx.update(boxRef, { content: newContent });
    });
  });

  await Promise.all(txs);
}
